<template>
  <v-card flat class="mt-5 px-5 pb-8">
    <v-row>
      <v-col cols="12" md="10" offset-md="1">
        <p class="font-weight-medium title mb-1">
          {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.title") }}
        </p>
        <p class="text-body-2 mb-4">
          {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.hint") }}
        </p>

        <div v-if="user.public_url" class="public-link d-flex align-center flex-wrap mb-6">
          <a :href="user.public_url" target="_blank" rel="noopener" class="public-link__url">{{ user.public_url }}</a>
          <v-btn small depressed color="primary" class="ml-3" @click="copyLink">
            <v-icon left small>mdi-content-copy</v-icon>
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.copy") }}
          </v-btn>
        </div>

        <v-form ref="form" :disabled="!isEdit">
          <v-combobox
            v-model="specialty"
            :search-input.sync="specialtySearch"
            :items="specialtyOptions"
            :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.specialty')"
            :hint="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.specialtyHint')"
            persistent-hint
            outlined
            :rules="specialtyRules"
            counter="100"
          ></v-combobox>

          <v-text-field
            v-model="languages"
            class="mt-4"
            :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.languages')"
            :hint="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.languagesHint')"
            persistent-hint
            outlined
            :rules="languagesRules"
            counter="255"
          ></v-text-field>

          <v-autocomplete
            v-model="workCountry"
            class="mt-4"
            :items="countryOptions"
            item-text="name"
            item-value="code"
            :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.workCountry')"
            :hint="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.workCountryHint')"
            persistent-hint
            outlined
          ></v-autocomplete>

          <v-checkbox
            v-model="introSession"
            class="mt-4"
            :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.introSession')"
            :hint="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.introSessionHint')"
            persistent-hint
          ></v-checkbox>

          <p class="font-weight-medium subtitle-1 mt-8 mb-2">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.contacts") }}
          </p>
          <p class="text-body-2 mb-4">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.contactsHint") }}
          </p>
          <v-row>
            <v-col
              v-for="field in contactFields"
              :key="field.key"
              cols="12"
              md="6"
              class="py-1"
            >
              <v-text-field
                v-model="contacts[field.key]"
                :label="field.label"
                :placeholder="field.placeholder"
                :prepend-inner-icon="field.icon"
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <p class="font-weight-medium subtitle-1 mt-8 mb-2">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.offline") }}
          </p>
          <p class="text-body-2 mb-4">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.offlineHint") }}
          </p>
          <v-row
            v-for="(item, index) in offlineLocations"
            :key="'offline' + index"
            class="align-center"
          >
            <v-col cols="12" md="3" class="py-1">
              <v-autocomplete
                v-model="item.country"
                :items="countryOptions"
                item-text="name"
                item-value="code"
                :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.country')"
                :rules="[requiredRule]"
                outlined
                dense
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="py-1">
              <!-- free text; the browser suggests cities other psychologists already entered -->
              <v-text-field
                v-model="item.city"
                :list="'offline-cities-' + index"
                :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.city')"
                :rules="[requiredRule, noRussianRule, cityNotOnlineRule]"
                maxlength="80"
                autocomplete="off"
                outlined
                dense
              ></v-text-field>
              <datalist :id="'offline-cities-' + index">
                <option v-for="city in citySuggestions(item.country)" :key="city" :value="city"></option>
              </datalist>
            </v-col>
            <v-col cols="10" md="5" class="py-1">
              <v-text-field
                v-model="item.address"
                :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.address')"
                :rules="[noRussianRule]"
                counter="190"
                maxlength="190"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="2" md="1" class="py-1 text-right">
              <v-btn icon color="red" :disabled="!isEdit" @click="offlineLocations.splice(index, 1)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn
            color="primary"
            outlined
            small
            :disabled="!isEdit || offlineLocations.length >= 5"
            @click="addOfflineLocation"
          >
            <v-icon left small>mdi-plus</v-icon>
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.addOffline") }}
          </v-btn>

          <p class="font-weight-medium subtitle-1 mt-8 mb-2">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.prices") }}
          </p>
          <p class="text-body-2 mb-4">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.pricesHint") }}
          </p>
          <v-alert v-if="hasLegacyCurrency" type="warning" text dense class="mb-4">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.pricesUahOnly") }}
          </v-alert>
          <v-row
            v-for="(item, index) in servicePrices"
            :key="index"
            class="align-center"
          >
            <v-col cols="12" md="6" class="py-1">
              <v-text-field
                v-model="item.title"
                :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.priceTitle')"
                :rules="[requiredRule, noRussianRule]"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="5" md="2" class="py-1">
              <v-text-field
                v-model.number="item.duration"
                type="number"
                min="5"
                :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.priceDuration')"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="5" md="3" class="py-1">
              <!-- prices are in hryvnias only; an old USD/EUR price must be entered again in hryvnias -->
              <v-text-field
                v-model.number="item.price"
                type="number"
                min="0"
                :label="$vuetify.lang.t('$vuetify.main.psychologistDashboard.publicPage.priceAmount')"
                :rules="[requiredRule, () => legacyPriceRule(item)]"
                :error-messages="legacyPriceMessage(item)"
                suffix="₴"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="2" md="1" class="py-1 text-right">
              <v-btn icon color="red" :disabled="!isEdit" @click="servicePrices.splice(index, 1)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn
            color="primary"
            outlined
            small
            :disabled="!isEdit || servicePrices.length >= 15"
            @click="addPrice"
          >
            <v-icon left small>mdi-plus</v-icon>
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.addPrice") }}
          </v-btn>
        </v-form>

        <div class="d-flex justify-end mt-8">
          <v-btn depressed color="success" :loading="saving" @click="save">
            {{ $vuetify.lang.t("$vuetify.main.psychologistDashboard.settings.save") }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import PsychologistServices from "@/services/api/psychologist.services";
import { noRussianRule } from "@/utils/noRussian";
import { countryOptions } from "@/utils/countries";

// ISO 3166 codes; names come from the browser (Intl.DisplayNames)

const CONTACT_FIELDS = [
  { key: "phone", icon: "mdi-phone", placeholder: "+380..." },
  { key: "email", icon: "mdi-email-outline", placeholder: "name@example.com" },
  { key: "telegram", icon: "mdi-send", placeholder: "@username" },
  { key: "viber", icon: "mdi-phone-message", placeholder: "+380..." },
  { key: "whatsapp", icon: "mdi-whatsapp", placeholder: "+380..." },
  { key: "instagram", icon: "mdi-instagram", placeholder: "@username" },
  { key: "facebook", icon: "mdi-facebook", placeholder: "https://facebook.com/..." },
  { key: "tiktok", icon: "mdi-music-note", placeholder: "@username" },
  { key: "youtube", icon: "mdi-youtube", placeholder: "https://youtube.com/..." },
  { key: "linkedin", icon: "mdi-linkedin", placeholder: "https://linkedin.com/in/..." },
  { key: "website", icon: "mdi-web", placeholder: "https://..." },
];

export default {
  name: "PublicPageSettings",
  data() {
    return {
      isEdit: true,
      loaded: false,
      saving: false,
      specialty: "",
      specialtySearch: null,
      languages: "",
      workCountry: "UA",
      introSession: false,
      contacts: {},
      servicePrices: [],
      offlineLocations: [],
      knownCities: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "user/getUser",
    }),
    contactFields() {
      return CONTACT_FIELDS.map((field) => ({
        ...field,
        label: this.$vuetify.lang.t(
          `$vuetify.main.psychologistDashboard.publicPage.contact.${field.key}`
        ),
      }));
    },
    specialtyOptions() {
      return this.$vuetify.lang
        .t("$vuetify.main.psychologistDashboard.publicPage.specialtyOptions")
        .split("|");
    },
    specialtyRules() {
      return [
        (v) =>
          !v ||
          String(v).length <= 100 ||
          this.$vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.tooLong"),
        noRussianRule,
      ];
    },
    // countries by name in Ukrainian; Ukraine and the neighbours first
    countryOptions() {
      return countryOptions();
    },
    languagesRules() {
      return [
        (v) =>
          !v ||
          String(v).length <= 255 ||
          this.$vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.languagesTooLong"),
        noRussianRule,
      ];
    },
    hasLegacyCurrency() {
      return this.servicePrices.some((item) => item.legacyCurrency);
    },
  },
  watch: {
    // підтягнути дані, коли профіль прийшов після відкриття вкладки; далі не перезаписувати введене
    user() {
      if (!this.loaded) {
        this.load();
      }
    },
  },
  mounted() {
    this.load();
    // cities other psychologists already entered: suggestions keep one spelling per city
    PsychologistServices.offlineLocations()
      .then((response) => {
        this.knownCities = response.data.data || [];
      })
      .catch(() => {});
  },
  methods: {
    noRussianRule,
    // an old USD/EUR price is kept as a number, but it has to be typed again in hryvnias
    legacyPriceMessage(item) {
      if (!item.legacyCurrency || Number(item.price) !== Number(item.legacyPrice)) {
        return [];
      }
      return [
        this.$vuetify.lang
          .t("$vuetify.main.psychologistDashboard.publicPage.priceWasInCurrency")
          .replace("{price}", `${item.legacyPrice} ${item.legacyCurrency}`),
      ];
    },
    legacyPriceRule(item) {
      const messages = this.legacyPriceMessage(item);
      return messages.length ? messages[0] : true;
    },
    // "Онлайн" typed as a city became a city in the catalog filter (customer 02.11)
    cityNotOnlineRule(v) {
      return (
        !/он[\s-]*ла[йи]н|on[\s-]*line/i.test(String(v || "")) ||
        this.$vuetify.lang.t("$vuetify.main.psychologistDashboard.publicPage.cityNotOnline")
      );
    },
    requiredRule(v) {
      return (
        (v !== null && v !== undefined && v !== "") ||
        this.$vuetify.lang.t("$vuetify.main.psychologistDashboard.settings.rules")
      );
    },
    load() {
      if (this.user && this.user.id) this.loaded = true;
      this.specialty = this.user.specialty || "";
      this.languages = this.user.languages || "";
      this.workCountry = this.user.work_country || "UA";
      this.introSession = !!this.user.intro_session;
      const contacts = {};
      CONTACT_FIELDS.forEach((field) => {
        contacts[field.key] = (this.user.contacts || {})[field.key] || "";
      });
      this.contacts = contacts;
      this.servicePrices = (this.user.service_prices || []).map((item) => {
        const currency = item.currency || "UAH";
        return {
          ...item,
          currency: "UAH",
          legacyCurrency: currency !== "UAH" ? currency : null,
          legacyPrice: currency !== "UAH" ? item.price : null,
        };
      });
      this.offlineLocations = (this.user.offline_locations || []).map((item) => ({
        country: item.country,
        city: item.city,
        address: item.address || "",
      }));
    },
    citySuggestions(country) {
      const group = this.knownCities.find((item) => item.country === country);
      return group ? group.cities.map((city) => city.name) : [];
    },
    addOfflineLocation() {
      this.offlineLocations.push({ country: "UA", city: "", address: "" });
    },
    addPrice() {
      this.servicePrices.push({
        title: "",
        duration: 50,
        price: null,
        currency: "UAH",
      });
    },
    copyLink() {
      navigator.clipboard.writeText(this.user.public_url).then(() => {
        Vue.swal({
          target: document.getElementById("main"),
          text: this.$vuetify.lang.t(
            "$vuetify.main.psychologistDashboard.publicPage.copied"
          ),
          icon: "success",
          timer: 2500,
          toast: true,
          position: "top-right",
          showConfirmButton: false,
        });
      });
    },
    save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // text typed into the combobox is committed only on blur
      if (this.specialtySearch !== null && this.specialtySearch !== this.specialty) {
        this.specialty = this.specialtySearch;
      }


      const contacts = {};
      Object.keys(this.contacts).forEach((key) => {
        contacts[key] = this.contacts[key] || null;
      });

      this.saving = true;
      this.$store
        .dispatch("user/updateProfile", {
          profile: {
            specialty: this.specialty || null,
            languages: (this.languages || "").trim() || null,
            work_country: this.workCountry || "UA",
            intro_session: !!this.introSession,
            contacts,
            service_prices: this.servicePrices.map((item) => ({
              title: item.title,
              price: item.price,
              duration: item.duration,
              currency: "UAH",
            })),
            offline_locations: this.offlineLocations
              .filter((item) => item.country && String(item.city || "").trim())
              .map((item) => ({
                country: item.country,
                city: String(item.city).trim(),
                address: String(item.address || "").trim() || null,
              })),
          },
        })
        .then((response) => {
          this.$store.commit("user/UPDATE_USER", response.data);
          this.load();
          Vue.swal({
            target: document.getElementById("main"),
            text: response.data.message,
            icon: "success",
            timer: 4000,
            toast: true,
            position: "top-right",
            showConfirmButton: false,
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.public-link {
  row-gap: 8px;
  padding: 12px 16px;
  border: 1px solid #d9e4eb;
  border-radius: 8px;
}
.public-link__url {
  word-break: break-all;
}
</style>
